import { useEffect, useState, useRef } from 'react';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { getUsers, getTotalUserCount } from '@/services/adminService';
import type { AppUser } from '@dnyaneshwari/shared';
import { formatRelativeTime } from '@/utils/dates';
import type { DocumentSnapshot } from 'firebase/firestore';

function formatMs(ms: number): string {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m`;
}

type SortKey = 'lastActiveAt' | 'displayName' | 'platform' | 'totalReadingTimeMs' | 'totalListeningTimeMs';

/** Firestore timestamps, Date objects and raw millis all reach this page. */
function toMillis(v: unknown): number {
  if (!v) return 0;
  if (v instanceof Date) return v.getTime();
  if (typeof v === 'number') return v;
  const ts = v as { toDate?: () => Date; seconds?: number };
  if (typeof ts.toDate === 'function') return ts.toDate().getTime();
  if (typeof ts.seconds === 'number') return ts.seconds * 1000;
  return 0;
}

export default function UsersPage() {
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('lastActiveAt');
  const [sortAsc, setSortAsc] = useState(false);
  const [total, setTotal] = useState<number | null>(null);
  const [fetchingAll, setFetchingAll] = useState(false);
  const lastDocRef = useRef<DocumentSnapshot | null>(null);
  // Guards the catch-up fetch against a second keystroke starting it again.
  const fetchingAllRef = useRef(false);

  async function load(reset = false) {
    setLoading(true);
    if (reset) { lastDocRef.current = null; fetchingAllRef.current = false; }
    try {
      const { users: fetched, last } = await getUsers(20, reset ? undefined : lastDocRef.current ?? undefined);
      setUsers((prev) => reset ? fetched : [...prev, ...fetched]);
      lastDocRef.current = last;
      setHasMore(last !== null);
    } finally {
      setLoading(false);
    }
  }

  /**
   * Searching the whole collection, not the page on screen.
   *
   * Firestore has no substring search -- "9890" sits in the middle of
   * +919890490929, so even a prefix query would not find it -- and the list
   * pages 20 at a time. Filtering what happened to be loaded meant a search
   * quietly missed everyone below the fold, which reads as "that person is
   * not a user" rather than "press Load more five more times".
   *
   * So the first keystroke pulls the rest in, in pages of 500. It costs one
   * read per user, once per visit to this page, and only when you actually
   * search. The cap is there so this cannot turn into an unbounded read if
   * the collection grows by an order of magnitude; past it, Load more still
   * works by hand.
   */
  const SEARCH_FETCH_CAP = 20000;

  async function fetchAllForSearch() {
    if (fetchingAllRef.current) return;
    fetchingAllRef.current = true;
    setFetchingAll(true);
    try {
      while (lastDocRef.current && users.length < SEARCH_FETCH_CAP) {
        const { users: page, last } = await getUsers(500, lastDocRef.current);
        lastDocRef.current = last;
        setUsers((prev) => [...prev, ...page]);
        setHasMore(last !== null);
        if (!last) break;
      }
    } finally {
      fetchingAllRef.current = false;
      setFetchingAll(false);
    }
  }

  useEffect(() => {
    load(true);
    void getTotalUserCount().then(setTotal).catch(() => {});
  }, []);

  useEffect(() => {
    if (search.trim() && hasMore) void fetchAllForSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, hasMore]);

  // Filter and sort the rows already fetched. Firestore cannot do substring
  // search, and paging is client-driven here, so both run in memory -- fine at
  // this scale, and it keeps "Load more" and search from fighting each other.
  const term = search.trim().toLowerCase();
  const visible = users
    .filter((u) =>
      !term ||
      (u.displayName || '').toLowerCase().includes(term) ||
      (u.phoneNumber || '').toLowerCase().includes(term)
    )
    .sort((a, b) => {
      let cmp: number;
      if (sortKey === 'displayName') {
        cmp = (a.displayName || '').localeCompare(b.displayName || '');
      } else if (sortKey === 'platform') {
        cmp = (a.platform || '').localeCompare(b.platform || '');
      } else if (sortKey === 'lastActiveAt') {
        cmp = toMillis(a.lastActiveAt) - toMillis(b.lastActiveAt);
      } else {
        cmp = ((a[sortKey] as number) || 0) - ((b[sortKey] as number) || 0);
      }
      return sortAsc ? cmp : -cmp;
    });

  function toggleSort(key: SortKey) {
    if (key === sortKey) {
      setSortAsc((v) => !v);
    } else {
      setSortKey(key);
      // Names read naturally A-Z; every other column is most-interesting-first.
      setSortAsc(key === 'displayName');
    }
  }

  function SortHeader({ label, keyName }: { label: string; keyName: SortKey }) {
    const active = sortKey === keyName;
    return (
      <th className="text-left px-4 py-3">
        <button
          onClick={() => toggleSort(keyName)}
          className={`inline-flex items-center gap-1 text-xs font-semibold uppercase transition-colors ${
            active ? 'text-amber-700' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          {label}
          <span aria-hidden="true" className={active ? 'opacity-100' : 'opacity-30'}>
            {active && sortAsc ? '▲' : '▼'}
          </span>
        </button>
      </th>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Users</h1>
          <p className="text-sm text-gray-500 mt-1">Manage users and view engagement data</p>
        </div>
        <button
          onClick={() => load(true)}
          className="px-4 py-2 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      {/* Search covers every user, not the rows on screen: typing pulls the
          rest of the collection in first. See fetchAllForSearch. */}
      <div className="relative mb-4">
        <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        <input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search every user by name or phone number…"
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500/40 focus:border-amber-400"
        />
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50">
              <SortHeader label="User" keyName="displayName" />
              <SortHeader label="Platform" keyName="platform" />
              <SortHeader label="Reading" keyName="totalReadingTimeMs" />
              <SortHeader label="Listening" keyName="totalListeningTimeMs" />
              <SortHeader label="Last Active" keyName="lastActiveAt" />
            </tr>
          </thead>
          <tbody>
            {loading && users.length === 0 ? (
              [...Array(5)].map((_, i) => (
                <tr key={i} className="border-b border-gray-100">
                  {[...Array(5)].map((_, j) => (
                    <td key={j} className="px-4 py-3">
                      <div className="h-4 bg-gray-100 rounded animate-pulse" />
                    </td>
                  ))}
                </tr>
              ))
            ) : visible.length === 0 ? (
              <tr>
                <td colSpan={5} className="text-center py-16 text-gray-400 text-sm">
                  {term
                    ? `No users match “${search.trim()}”.`
                    : 'No users yet. Share the app to get started.'}
                </td>
              </tr>
            ) : (
              visible.map((u) => (
                <tr key={u.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold text-sm flex-shrink-0">
                        {(u.displayName || u.phoneNumber || '?')[0].toUpperCase()}
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{u.displayName || '—'}</p>
                        <p className="text-xs text-gray-400">{u.phoneNumber}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                      u.platform === 'android' ? 'bg-green-100 text-green-700' :
                      u.platform === 'ios' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-600'
                    }`}>
                      {u.platform}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatMs(u.totalReadingTimeMs || 0)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {formatMs(u.totalListeningTimeMs || 0)}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-400">
                    {formatRelativeTime(u.lastActiveAt)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>

        {/* Says what the count refers to, so a filtered view is never mistaken
            for the whole user base. */}
        {!loading && users.length > 0 && (
          <div className="px-4 py-2.5 border-t border-gray-100 text-xs text-gray-400">
            {fetchingAll
              ? `Loading the rest of the users to search… ${users.length}${total ? ` of ${total}` : ''}`
              : term
                ? `${visible.length} match${visible.length === 1 ? '' : 'es'} out of ${users.length} user${users.length === 1 ? '' : 's'} searched`
                : `${users.length} of ${total ?? users.length} user${total === 1 ? '' : 's'} loaded`}
          </div>
        )}

        {hasMore && (
          <div className="px-4 py-3 border-t border-gray-100">
            <button
              onClick={() => load(false)}
              disabled={loading}
              className="text-sm text-amber-600 font-medium hover:underline disabled:opacity-50"
            >
              {loading ? 'Loading…' : 'Load more'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
